import { CLIP_STATUS, CONTENT_TYPES, EVENT_NAME, HIGHLIGHT_GENERATION_STATUS, SYNCING_STATUS } from "constants/phase";
import { clipPageTabOptions } from "constants/v2/appConstants";
import { isClipHasMetadata } from "helpers";
import { createSelector } from "reselect";

import { selectClipDetailsById, selectClipEntities } from "../clip/selectors";
import { selectStreamDetailsById } from "../stream/selectors";
import { clipListEntityAdapter, clipListSlice } from "./slice";

const selectClipListState = (state) => state[clipListSlice.name];

export const selectClipListModals = createSelector(selectClipListState, (clipListState) => clipListState.modals);

export const selectClipListIds = createSelector(selectClipListState, (clipListState) =>
  clipListEntityAdapter.getSelectors().selectIds(clipListState),
);

const selectAllClipListDetails = createSelector(selectClipListState, (clipListState) =>
  clipListEntityAdapter.getSelectors().selectAll(clipListState),
);
export const selectClipListEntities = createSelector(selectClipListState, (clipListState) =>
  clipListEntityAdapter.getSelectors().selectEntities(clipListState),
);

export const selectGeneratedClipListIds = createSelector(selectAllClipListDetails, (allClipListDetails) =>
  allClipListDetails
    .filter(
      (clipListDetail) =>
        (clipListDetail?.previewUrl || clipListDetail?.previewUrl === "") &&
        clipListDetail?.type !== clipPageTabOptions.webStory,
    )
    .map((filteredClipList) => filteredClipList._id),
);

export const selectDraftClipListIds = createSelector(selectAllClipListDetails, (allClipListDetails) =>
  allClipListDetails
    .filter(
      (clipListDetail) =>
        clipListDetail?.previewUrl !== "" &&
        !clipListDetail?.previewUrl &&
        !clipListDetail?.automationIdentifier &&
        clipListDetail?.type !== clipPageTabOptions.webStory,
    )
    .map((filteredClipList) => filteredClipList._id),
);
export const selectFromTheRulesClipListIds = createSelector(selectAllClipListDetails, (allClipListDetails) =>
  allClipListDetails
    .filter(
      (clipListDetail) => clipListDetail?.automationIdentifier && clipListDetail?.type !== clipPageTabOptions.webStory,
    )
    .map((filteredClipList) => filteredClipList._id),
);

export const selectClipListCount = createSelector(selectClipListState, (clipListState) => clipListState.count);

export const selectClipListDetailsById = createSelector(
  selectClipListState,
  (state, props) => ({
    clipListId: props.clipListId,
    clipListData: props.clipListData,
    contentId: props.contentId,
    contentType: props.contentType,
  }),
  (clipListState, { clipListId, clipListData, contentId, contentType }) => {
    if (!clipListId && contentId && contentType === CONTENT_TYPES.HIGHLIGHT) {
      clipListId = contentId;
    }

    return clipListData || clipListEntityAdapter.getSelectors().selectById(clipListState, clipListId) || null;
  },
);

export const selectClipListCreatedAtById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.createdAt,
);

export const selectClipListUpdatedAtById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.updatedAt,
);

export const selectClipListClipsById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.clips || [],
);

export const selectClipListClipsWithDetailsById = createSelector(
  selectClipListClipsById,
  selectClipEntities,
  (clipListClips, clipEntities) => {
    if (clipListClips?.length) {
      return clipListClips.map((clipId) => clipEntities[clipId]);
    }

    return [];
  },
);

export const selectClipListDetailsWithClipDetailsById = createSelector(
  selectClipListDetailsById,
  selectClipEntities,
  (clipListDetails, clipEntities) => {
    if (clipListDetails?.clips?.length) {
      return {
        ...clipListDetails,
        clips: clipListDetails.clips.map((clipId) => clipEntities[clipId]),
      };
    }

    return clipListDetails;
  },
);

export const selectClipListEditedVideos = createSelector(
  selectClipListDetailsWithClipDetailsById,
  (clipListDetails) => {
    if (clipListDetails?.clips?.length) {
      return clipListDetails.clips.map((clip) => {
        return clip?.editedVideos || [];
      });
    }

    return [];
  },
);

export const selectClipListAspectRatioById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.aspectRatio,
);

export const selectClipListWithAspectRatiosAndEditedVideosById = createSelector(
  selectClipListClipsWithDetailsById,
  (clipListClipsWithDetails) => {
    return clipListClipsWithDetails.map((clip) => ({
      aspectRatio: clip?.aspectRatio,
      editedVideos: clip?.editedVideos,
    }));
  },
);

export const selectIsAllClipsOfClipListSyncedWithFoldersAspectRatio = createSelector(
  selectClipListWithAspectRatiosAndEditedVideosById,
  selectClipListAspectRatioById,
  (clipListWithAspectRatiosAndEditedVideos, aspectRatio) => {
    /*
      NOTE:

      A clip is considered synced with the folder's aspect ratio if:
      1. The clip's current aspect ratio matches the folder's aspect ratio OR
      2. It has an edited video that:
         - Matches the folder's aspect ratio
         - Is in COMPLETED status
         - Is not a dynamic event
      
      The selector returns true if all clips in the list meet these conditions.
    */

    return clipListWithAspectRatiosAndEditedVideos.every(
      (clip) =>
        clip?.aspectRatio === aspectRatio ||
        clip?.editedVideos?.some(
          ({ aspect_ratio, status, event }) =>
            aspect_ratio === aspectRatio && status === SYNCING_STATUS.COMPLETED && event !== EVENT_NAME.DYNAMIC,
        ),
    );
  },
);

export const selectClipListSyncingInProgress = createSelector(
  selectClipListWithAspectRatiosAndEditedVideosById,
  selectClipListAspectRatioById,
  (clipListWithAspectRatiosAndEditedVideos, aspectRatio) => {
    /*
      NOTE:

      Check if any clip in the clip list is currently being synced with the folder's aspect ratio.
      A clip is considered to be syncing if:
      1. The clip's current aspect ratio doesn't match the folder's aspect ratio AND
      2. It has an edited video that:
         - Matches the folder's aspect ratio
         - Is in PROCESSING status
         - Is not a dynamic event

      The selector returns true if at least one clip in the list meets these conditions.
    */

    return clipListWithAspectRatiosAndEditedVideos.some((clip) => {
      if (clip?.aspectRatio === aspectRatio) {
        return false;
      }

      return clip?.editedVideos?.some(
        ({ aspect_ratio, status, event }) =>
          aspect_ratio === aspectRatio && status === SYNCING_STATUS.PROCESSING && event !== EVENT_NAME.DYNAMIC,
      );
    });
  },
);

export const selectClipListDurationById = createSelector(
  selectClipListDetailsById,
  selectClipEntities,
  (clipDetails, clipEntities) => {
    if (clipDetails?.totalDuration) {
      return clipDetails?.totalDuration;
    }
    if (clipDetails?.clips?.length) {
      return clipDetails.clips
        .map((clipId) => clipEntities[clipId])
        .reduce((acc, clipDetails) => acc + (clipDetails?.duration || 0), 0);
    }

    return 0;
  },
);

export const selectClipListGeneratedDurationById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.generatedDuration,
);

export const selectClipListFinalDurationById = createSelector(
  selectClipListDurationById,
  selectClipListGeneratedDurationById,
  (clipListDuration, generatedDuration) => generatedDuration || clipListDuration,
);

export const selectClipsIncludedInGeneratedHighlight = createSelector(
  selectClipListClipsWithDetailsById,
  selectClipListDetailsById,
  (clipListClips, clipListDetails) => {
    const clipIdArr = clipListDetails?.previewData?.clipIdArr || [];
    const clipIdArray = Array.isArray(clipIdArr) ? clipIdArr : [];

    return clipListClips?.filter((clip) => clipIdArray.includes(clip?.id)) || [];
  },
);

export const selectClipListTitleById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.title,
);

export const selectClipListIsPreviewById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.isPreview,
);

export const selectClipListPreviewDataById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.previewData || {},
);

export const selectClipListPreviewUrlById = createSelector(
  selectClipListDetailsById,
  selectClipListPreviewDataById,
  (clipListDetails, clipListPreviewData) => clipListDetails?.previewUrl || clipListPreviewData?.previewUrl,
);

export const selectClipListPreviewDataDurationById = createSelector(
  selectClipListPreviewDataById,
  (previewData) => previewData?.duration || 0,
);

export const selectClipListClipsStreamId = createSelector(selectClipListClipsWithDetailsById, (clipListClips) => {
  const streamIds = [...new Set(clipListClips.map((clip) => clip.streamId))];

  return streamIds;
});

export const selectClipListStreamIdById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.streamId,
);

export const selectClipListAutoTranscriptApiById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.audioTranscript_api,
);

export const selectClipListFolderRatingById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.folderRating,
);
export const selectClipListFolderReasonForRatingById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.reasonForRating,
);

export const selectClipListThumbnailUrls = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.thumbnails,
);
export const selectClipListThumbnailUrl = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.thumbnail,
);

export const selectClipListEntityId = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.entityId,
);
export const selectClipListClipsCustomData = createSelector(
  selectClipListDetailsWithClipDetailsById,
  (clipListDetails) => clipListDetails?.clips?.[0]?.customData,
);
export const selectClipListClipCsvData = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.csvData || {},
);

export const selectIsAudioTranscriptApiAvailableInHighlightClips = createSelector(
  selectClipListClipsWithDetailsById,
  (clipListClips) =>
    Array.isArray(clipListClips) && clipListClips.length > 0
      ? clipListClips.every((clip) => clip?.audioTranscript_api?.segments?.length > 0)
      : false,
);

export const selectClipListAudioTranscriptApi = createSelector(selectClipListClipsWithDetailsById, (clipListClips) => {
  if (!clipListClips || clipListClips.length === 0) {
    return [];
  }

  const mergedSegments = clipListClips.reduce((acc, clip) => {
    if (clip?.audioTranscript_api?.segments?.length) {
      acc.push(...clip.audioTranscript_api.segments);
    }

    return acc;
  }, []);

  return mergedSegments;
});

export const selectClipListClipsIdArr = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.previewData?.clipIdArr,
);

// const hasAudioTranscript = (clip) => {
//   return clip?.audioTranscript_api?.segments?.length;
// };

export const selectAllClipsOfClipListHaveTranscriptOrTags = createSelector(
  [selectClipListClipsWithDetailsById],
  (clipListClips) => {
    if (!clipListClips?.length) return false;

    return clipListClips.every((clip) => {
      const hasTags = isClipHasMetadata(clip);
      // const hasTranscript = hasAudioTranscript(clip);

      return hasTags;
    });
  },
);

export const selectClipListCreateModal = createSelector(selectClipListModals, (modals) => modals.clipListCreateModal);

export const selectAddClipToClipListModal = createSelector(
  selectClipListModals,
  (modals) => modals.addClipToClipListModal,
);

export const selectClipListDeleteModal = createSelector(selectClipListModals, (modals) => modals.clipListDeleteModal);

export const selectAssignRatingModal = createSelector(selectClipListModals, (modals) => modals.assignRatingModal);

export const selectRenameClipListModal = createSelector(selectClipListModals, (modals) => modals.renameClipListModal);

export const selectClipListAllFiltersModal = createSelector(
  selectClipListModals,
  (modals) => modals.clipListAllFiltersModal,
);

export const selectUploadFilesModal = createSelector(
  selectClipListModals,
  (modals) => modals.editingSidebarContentUploadModal,
);

export const selectClipListTemplateApplyModal = createSelector(
  selectClipListModals,
  (modals) => modals.clipListTemplateApplyModal,
);

export const selectClipListSelectVariantModal = createSelector(
  selectClipListModals,
  (modals) => modals.clipListSelectVariantModal,
);

export const selectClipListIdsByDateId = createSelector(
  selectClipListState,
  (state, props) => props.clipListDateId,
  (clipListState, clipListDateId) => clipListState?.groupByDate[clipListDateId]?.data,
);

export const selectGroupByDateListIds = createSelector(selectClipListState, (clipListState) =>
  Object.keys(clipListState?.groupByDate),
);

export const selectGeneratedHLCounts = createSelector(
  selectClipListState,
  (clipListState) => clipListState?.generatedHighlightCount,
);
export const selecDraftsHLCounts = createSelector(
  selectClipListState,
  (clipListState) => clipListState?.draftHighlightCount,
);
export const selecFromTheRulesHLCounts = createSelector(
  selectClipListState,
  (clipListState) => clipListState?.fromTheRulesHighlightCount,
);
export const selectClipListRegenerateHighlightModal = createSelector(
  selectClipListModals,
  (modals) => modals.clipListRegenerateHighlightModal,
);
export const selectClipListCancelHighlightModal = createSelector(
  selectClipListModals,
  (modals) => modals.clipListCancelHighlightModal,
);

export const selectClipListIdsCount = createSelector(selectClipListIds, (clipListIds) => clipListIds?.length);

export const selectClipListSearchResultIds = createSelector(
  selectClipListState,
  (clipListState) => clipListState?.searchResultIds,
);

export const selectStreamDetailsByClipListId = createSelector(
  [(state) => state, selectClipListStreamIdById],
  (state, streamId) => {
    if (!streamId) return null;

    return selectStreamDetailsById(state, { streamId });
  },
);

export const selectClipListStreamTitleById = createSelector(
  selectStreamDetailsByClipListId,
  selectClipListDetailsById,
  (streamDetails, clipListDetails) => streamDetails?.title || clipListDetails?.stream?.title,
);

export const selectTournamentIdByClipListIdStreamDetails = createSelector(
  selectStreamDetailsByClipListId,
  (streamDetail) => {
    return streamDetail?.fields?.tournamentId;
  },
);

export const selectFirstClipIdByClipListId = createSelector(selectClipListClipsById, (clipListClips) => {
  if (!clipListClips?.length) return null;

  return clipListClips[0];
});

export const selectFirstClipDetailsByClipListId = createSelector(
  [(state) => state, selectFirstClipIdByClipListId],
  (state, clipId) => {
    if (!clipId) return null;

    return selectClipDetailsById(state, { clipId });
  },
);

export const selectClipListCompletedAtById = createSelector(
  selectClipListDetailsById,
  (clipListDetails) => clipListDetails?.completedAt,
);

export const selectIsClipListClipsProcessing = createSelector(selectClipListClipsWithDetailsById, (clipListClips) => {
  return clipListClips.some((clip) => clip?.clipStatus === CLIP_STATUS.PROCESSING);
});

export const selectClipListStatusById = createSelector(selectClipListDetailsById, (clipListDetails) => {
  const statusMap = {
    InProgress: HIGHLIGHT_GENERATION_STATUS.IN_PROGRESS,
    Completed: HIGHLIGHT_GENERATION_STATUS.COMPLETED,
    Failed: HIGHLIGHT_GENERATION_STATUS.FAILED,
  };

  return statusMap[clipListDetails?.previewData?.status] || HIGHLIGHT_GENERATION_STATUS.DRAFT;
});

export const selectClipListProgressPercentById = createSelector(selectClipListDetailsById, (clipListDetails) => {
  return clipListDetails?.progressPercent || 0;
});
